// @soft-depth
import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ClipboardList, QrCode } from 'lucide-react';
import CoverQuizDialog from './CoverQuizDialog';
import CoverQuizInsights from './CoverQuizInsights';
import { markCoverQuizApplied, markCoverQuizDiscApplied, useCoverQuiz } from './coverQuizStorage';
import type { DiscLetter } from './coverQuizData';
import type { PromptInput } from './coverQuizImport';
import { pickCopy } from '../../utils/localeCopy';
import { IdeaCard } from '../../components/ClientEntry/SmartIdeaDialog/IdeaKit';
import LegacyThemeScope from '../../design-system/LegacyThemeScope';

/**
 * Smart IDEA 客戶概覽 — the 保障小測驗 imported: its insights when the client has
 * finished it, an invitation to send it when not. The same Soft Depth section
 * card (IdeaKit) as the rest of Smart IDEA; the send / resend dialog is legacy.
 */

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale?: string;
};

const CARD_COPY = {
  'zh-Hant-HK': {
    title: '理財小測驗',
    imported: ' · 已匯入',
    view: '查看／重新發送',
    send: '發送',
    empty: '客戶還未做理財小測驗。發送一個約 2 分鐘的小測驗，可以推斷性格、找出會面切入點和可能的顧慮。'
  },
  zh: {
    title: '理财小测验',
    imported: ' · 已导入',
    view: '查看／重新发送',
    send: '发送',
    empty: '客户还没做理财小测验。发送一个约 2 分钟的小测验，可以推断性格、找出会面切入点和可能的顾虑。'
  },
  en: {
    title: 'Money quiz',
    imported: ' · Imported',
    view: 'View / resend',
    send: 'Send',
    empty:
      "The client hasn't taken the money quiz yet. Send a 2-minute quiz to read their personality and find meeting openers and likely concerns."
  }
};

export interface CoverQuizCardProps {
  intl: IntlLike;
  clientId?: string | null;
  clientName?: string;
  currentDisc?: string | null;
  onApplyDisc?: (letter: DiscLetter) => void;
  currentPromptInput?: PromptInput | null;
  onImport?: (next: PromptInput) => void;
}

const CoverQuizCard = (props: CoverQuizCardProps) => {
  const { intl, clientId, clientName, currentDisc, onApplyDisc, currentPromptInput, onImport } = props;
  const ui = pickCopy(CARD_COPY, intl.locale);
  const result = useCoverQuiz(clientId);
  const [dialogOpen, setDialogOpen] = useState(false);
  if (!clientId) return null;

  const applyDisc = onApplyDisc
    ? (letter: DiscLetter) => {
        onApplyDisc(letter);
        markCoverQuizDiscApplied(clientId);
      }
    : undefined;
  // Same stamp CoverQuizDialog records, so an import from this card also reads as 已匯入.
  const importAnswers = onImport
    ? (next: PromptInput) => {
        onImport(next);
        markCoverQuizApplied(clientId);
      }
    : undefined;

  return (
    <IdeaCard
      id="cover-quiz"
      testId="cover-quiz-card"
      icon={<ClipboardList size={18} aria-hidden="true" />}
      title={`${ui.title}${result ? ui.imported : ''}`}
      action={
        <Button
          size="small"
          variant="outlined"
          data-testid="cover-quiz-card-open"
          startIcon={<QrCode size={14} aria-hidden="true" />}
          onClick={() => setDialogOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          {result ? ui.view : ui.send}
        </Button>
      }
    >
      {result ? (
        <CoverQuizInsights
          result={result}
          intl={intl}
          clientId={clientId}
          currentDisc={currentDisc}
          onApplyDisc={applyDisc}
          currentPromptInput={currentPromptInput}
          onImport={importAnswers}
        />
      ) : (
        <Typography component="p" variant="body2" sx={{ m: 0, color: 'text.secondary' }}>
          {ui.empty}
        </Typography>
      )}
      <LegacyThemeScope>
        <CoverQuizDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          intl={intl}
          clientId={clientId}
          clientName={clientName}
          currentDisc={currentDisc}
          onApplyDisc={onApplyDisc}
          currentPromptInput={currentPromptInput}
          onImport={onImport}
        />
      </LegacyThemeScope>
    </IdeaCard>
  );
};

export default CoverQuizCard;
